/**
 * THE UI SOUND BUS — every interface sound in the game, one mixer, one volume.
 *
 * ── WHAT IT IS ──────────────────────────────────────────────────────────────
 * A small WebAudio bus dedicated to the INTERFACE: click, hover tick, toggle,
 * tab switch, modal open/close, confirm, error, reward, coin, level-up,
 * purchase and notification. Its level is `master × ui` (the "Interface sounds"
 * slider in Settings), it obeys the shared mute, and it is completely separate
 * from the match mixer so a menu click never ducks the battle score and a
 * battle effect never steals a menu voice.
 *
 * ── THE MANIFEST: A FILE BEATS THE SYNTH, WITH NO CODE CHANGE ───────────────
 * Every id has a recipe here AND a file slot at `/audio/ui/<id>.ogg` (then
 * `.mp3`). The first time an id plays, the synth voices it immediately and the
 * bus probes the file slot in the background; if a real recording decodes, it
 * replaces the synth for the rest of the session. Drop a file at the path
 * `docs/ui/HIGGSFIELD-GENERATION-PLAN.md` lists and it is live on next load.
 * A missing file, a 404 and the dev server's `index.html`-with-a-200 all fail
 * to decode, which is the same outcome: the synth stays. Never a throw.
 *
 * ── WHY SYNTHESISED AND NOT SILENT ──────────────────────────────────────────
 * The owner's ask is sound on every control NOW, with generation after. The
 * recipes below are short (40–600 ms), soft-attacked and filtered: wooden
 * ticks, bell partials and filtered-noise air, not square-wave beeps. Each is
 * a list of layers, so a sound designer can tune one without reading DSP.
 *
 * ── THE AUTOPLAY LOCK ───────────────────────────────────────────────────────
 * The AudioContext is created on the first `unlockUiSounds()` call, which the
 * cue delegate makes from inside a real gesture. Before that every play is a
 * silent `false`. A cue is garnish; queueing it to fire a second late on a
 * different screen would be worse than dropping it.
 *
 * ── THROTTLING ──────────────────────────────────────────────────────────────
 * Per-id minimum intervals (a swept grid of hover ticks is one tick, not a
 * machine gun) plus a global cap on simultaneous voices.
 */
import { pageHasUserActivation } from './activation';
import { audioPrefsStore, type AudioPrefs } from './prefs';

/* ══════════════════════════════════════════════════════════════ the ids ══ */

export const UI_SOUND_IDS = Object.freeze([
  'click',
  'hover',
  'toggle',
  'tab',
  'modal_open',
  'modal_close',
  'confirm',
  'error',
  'reward',
  'coin',
  'level_up',
  'purchase',
  'notify',
] as const);

export type UiSoundId = (typeof UI_SOUND_IDS)[number];

export function isUiSoundId(id: string): id is UiSoundId {
  return (UI_SOUND_IDS as readonly string[]).includes(id);
}

/* ═══════════════════════════════════════════════════════════ the recipes ══ */

/** One voice of a recipe. Times in seconds, frequencies in Hz. */
export interface SynthLayer {
  readonly kind: 'sine' | 'triangle' | 'square' | 'sawtooth' | 'noise';
  /** Start frequency (oscillators) or filter centre (noise). */
  readonly freq: number;
  /** Glide target, reached at the end of the layer. */
  readonly to?: number;
  readonly delay?: number;
  readonly attack?: number;
  /** Exponential decay to silence after the attack. */
  readonly decay: number;
  readonly gain: number;
  /** Lowpass (oscillators) or bandpass Q (noise). */
  readonly filter?: number;
  readonly q?: number;
}

export interface UiSoundSpec {
  /** Bus-relative level before master × ui. */
  readonly gain: number;
  /** Two plays of this id closer together than this are one play. */
  readonly minIntervalMs: number;
  readonly layers: readonly SynthLayer[];
}

/** Note helper: semitones from A4. Keeps the chimes in one key (C major). */
const n = (semis: number): number => 440 * 2 ** (semis / 12);
const C5 = n(3);
const E5 = n(7);
const G5 = n(10);
const C6 = n(15);
const E6 = n(19);
const G6 = n(22);
const B5 = n(14);

export const UI_SOUND_MANIFEST: Readonly<Record<UiSoundId, UiSoundSpec>> = Object.freeze({
  // A dry wooden tick: a fast-falling triangle body and a short bright knock.
  click: {
    gain: 0.55,
    minIntervalMs: 45,
    layers: [
      { kind: 'triangle', freq: 1250, to: 520, attack: 0.002, decay: 0.05, gain: 0.7, filter: 3200 },
      { kind: 'noise', freq: 3800, attack: 0.001, decay: 0.022, gain: 0.35, q: 1.4 },
    ],
  },
  // Barely there: a felt tap, high and very short.
  hover: {
    gain: 0.22,
    minIntervalMs: 90,
    layers: [{ kind: 'sine', freq: 2100, to: 1900, attack: 0.003, decay: 0.03, gain: 0.6 }],
  },
  // Two quick rising blips — a latch closing.
  toggle: {
    gain: 0.5,
    minIntervalMs: 60,
    layers: [
      { kind: 'triangle', freq: 660, attack: 0.002, decay: 0.05, gain: 0.55, filter: 2600 },
      { kind: 'triangle', freq: 990, delay: 0.045, attack: 0.002, decay: 0.07, gain: 0.5, filter: 3000 },
      { kind: 'noise', freq: 2500, attack: 0.001, decay: 0.015, gain: 0.2, q: 2 },
    ],
  },
  // A soft page turn: band-passed air sweeping up, plus a tiny tick.
  tab: {
    gain: 0.45,
    minIntervalMs: 70,
    layers: [
      { kind: 'noise', freq: 900, to: 2600, attack: 0.012, decay: 0.09, gain: 0.5, q: 0.9 },
      { kind: 'triangle', freq: 1500, to: 900, delay: 0.02, attack: 0.002, decay: 0.04, gain: 0.35, filter: 3000 },
    ],
  },
  // A panel lifting: rising air and a warm fifth.
  modal_open: {
    gain: 0.45,
    minIntervalMs: 120,
    layers: [
      { kind: 'noise', freq: 500, to: 1800, attack: 0.03, decay: 0.16, gain: 0.45, q: 0.8 },
      { kind: 'sine', freq: C5, to: G5, attack: 0.01, decay: 0.18, gain: 0.35 },
    ],
  },
  // The same panel settling back down.
  modal_close: {
    gain: 0.4,
    minIntervalMs: 120,
    layers: [
      { kind: 'noise', freq: 1600, to: 450, attack: 0.01, decay: 0.12, gain: 0.4, q: 0.8 },
      { kind: 'sine', freq: G5, to: C5, attack: 0.005, decay: 0.12, gain: 0.28 },
    ],
  },
  // A bright major third: "yes, done".
  confirm: {
    gain: 0.5,
    minIntervalMs: 80,
    layers: [
      { kind: 'triangle', freq: C6, attack: 0.003, decay: 0.12, gain: 0.5, filter: 5000 },
      { kind: 'triangle', freq: E6, delay: 0.06, attack: 0.003, decay: 0.2, gain: 0.5, filter: 5000 },
      { kind: 'sine', freq: C6 * 2, delay: 0.06, attack: 0.002, decay: 0.08, gain: 0.12 },
    ],
  },
  // A muted wooden double-knock, falling — refusal without a buzzer.
  error: {
    gain: 0.55,
    minIntervalMs: 150,
    layers: [
      { kind: 'triangle', freq: 240, to: 190, attack: 0.003, decay: 0.09, gain: 0.7, filter: 1200 },
      { kind: 'triangle', freq: 200, to: 150, delay: 0.1, attack: 0.003, decay: 0.12, gain: 0.6, filter: 1000 },
      { kind: 'noise', freq: 700, attack: 0.001, decay: 0.03, gain: 0.25, q: 1.2 },
    ],
  },
  // A rising sparkle arpeggio, C–E–G–C.
  reward: {
    gain: 0.45,
    minIntervalMs: 120,
    layers: [
      { kind: 'triangle', freq: C6, attack: 0.002, decay: 0.12, gain: 0.45, filter: 6000 },
      { kind: 'triangle', freq: E6, delay: 0.055, attack: 0.002, decay: 0.12, gain: 0.45, filter: 6000 },
      { kind: 'triangle', freq: G6, delay: 0.11, attack: 0.002, decay: 0.14, gain: 0.45, filter: 6000 },
      { kind: 'sine', freq: C6 * 2, delay: 0.165, attack: 0.002, decay: 0.3, gain: 0.35 },
      { kind: 'noise', freq: 7000, delay: 0.1, attack: 0.02, decay: 0.25, gain: 0.08, q: 3 },
    ],
  },
  // The classic two-note coin, B then E, bell-ish.
  coin: {
    gain: 0.42,
    minIntervalMs: 55,
    layers: [
      { kind: 'square', freq: B5 * 2, attack: 0.001, decay: 0.06, gain: 0.18, filter: 4500 },
      { kind: 'square', freq: E6 * 2, delay: 0.06, attack: 0.001, decay: 0.22, gain: 0.2, filter: 5000 },
      { kind: 'sine', freq: E6 * 4, delay: 0.06, attack: 0.001, decay: 0.12, gain: 0.08 },
    ],
  },
  // A fanfare-lite: rising fifths and a held shimmering top.
  level_up: {
    gain: 0.5,
    minIntervalMs: 400,
    layers: [
      { kind: 'triangle', freq: C5, attack: 0.004, decay: 0.16, gain: 0.45, filter: 4000 },
      { kind: 'triangle', freq: G5, delay: 0.09, attack: 0.004, decay: 0.16, gain: 0.45, filter: 4000 },
      { kind: 'triangle', freq: C6, delay: 0.18, attack: 0.004, decay: 0.2, gain: 0.45, filter: 5000 },
      { kind: 'triangle', freq: E6, delay: 0.27, attack: 0.006, decay: 0.55, gain: 0.45, filter: 6000 },
      { kind: 'sine', freq: G6, delay: 0.27, attack: 0.02, decay: 0.6, gain: 0.22 },
      { kind: 'noise', freq: 6500, to: 9000, delay: 0.25, attack: 0.05, decay: 0.45, gain: 0.08, q: 2.5 },
    ],
  },
  // Coins into a purse, then the confirm chime.
  purchase: {
    gain: 0.5,
    minIntervalMs: 200,
    layers: [
      { kind: 'noise', freq: 5200, attack: 0.001, decay: 0.03, gain: 0.25, q: 4 },
      { kind: 'noise', freq: 4600, delay: 0.05, attack: 0.001, decay: 0.03, gain: 0.22, q: 4 },
      { kind: 'square', freq: B5 * 2, delay: 0.08, attack: 0.001, decay: 0.05, gain: 0.14, filter: 4500 },
      { kind: 'triangle', freq: C6, delay: 0.14, attack: 0.003, decay: 0.12, gain: 0.45, filter: 5000 },
      { kind: 'triangle', freq: G6, delay: 0.2, attack: 0.003, decay: 0.3, gain: 0.45, filter: 6000 },
    ],
  },
  // A soft two-tone bell with a partial — something arrived.
  notify: {
    gain: 0.42,
    minIntervalMs: 300,
    layers: [
      { kind: 'sine', freq: E5 * 2, attack: 0.002, decay: 0.25, gain: 0.45 },
      { kind: 'sine', freq: E5 * 2 * 2.76, attack: 0.002, decay: 0.08, gain: 0.08 },
      { kind: 'sine', freq: C6, delay: 0.12, attack: 0.002, decay: 0.35, gain: 0.45 },
      { kind: 'sine', freq: C6 * 2.76, delay: 0.12, attack: 0.002, decay: 0.1, gain: 0.07 },
    ],
  },
});

/** Where a recorded replacement for an id is looked for, in order. */
export const UI_SOUND_BASE = '/audio/ui/';
export function uiSoundFiles(id: UiSoundId): readonly string[] {
  return [`${UI_SOUND_BASE}${id}.ogg`, `${UI_SOUND_BASE}${id}.mp3`];
}

/** Hard cap on simultaneous UI voices. */
export const UI_MAX_VOICES = 6;

/* ═════════════════════════════════════════════════════════════ the bus ══ */

/** The parts of `AudioContext` the bus touches — narrow so tests can fake it. */
type Ctx = AudioContext;

export interface UiSoundBusDeps {
  /** Build the context. Defaults to `new AudioContext()`. Null → silent bus. */
  createContext?(): Ctx | null;
  /** Fetch a file slot. Defaults to `fetch`. */
  fetchFile?(url: string): Promise<ArrayBuffer | null>;
  now?(): number;
  prefs?(): AudioPrefs;
  subscribePrefs?(cb: (p: AudioPrefs) => void): () => void;
}

type FileState = 'unknown' | 'probing' | 'none';

export class UiSoundBus {
  private ctx: Ctx | null = null;
  private out: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private readonly last = new Map<UiSoundId, number>();
  private readonly buffers = new Map<UiSoundId, AudioBuffer>();
  private readonly files = new Map<UiSoundId, FileState>();
  private voices = 0;
  private unsub: (() => void) | null = null;
  private failed = false;

  constructor(private readonly deps: UiSoundBusDeps = {}) {}

  /** Has a running context. */
  get unlocked(): boolean {
    return this.ctx !== null && this.ctx.state !== 'closed';
  }

  /** The effective bus level right now, 0..1. */
  level(): number {
    const p = this.readPrefs();
    if (p.muted) return 0;
    return clamp01(p.master) * clamp01(p.ui);
  }

  /** MUST be called from inside a user gesture the first time. Idempotent. */
  unlock(): boolean {
    if (this.failed) return false;
    // Before the first gesture a context would be refused and logged; wait.
    if (!pageHasUserActivation()) return false;
    if (this.ctx === null) {
      const make = this.deps.createContext ?? defaultContext;
      const ctx = make();
      if (ctx === null) {
        this.failed = true;
        return false;
      }
      this.ctx = ctx;
      try {
        this.out = ctx.createGain();
        this.out.gain.value = this.level();
        this.out.connect(ctx.destination);
      } catch {
        this.failed = true;
        this.ctx = null;
        return false;
      }
      const sub = this.deps.subscribePrefs ?? ((cb) => audioPrefsStore.subscribe(cb));
      this.unsub = sub(() => this.applyLevel());
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => undefined);
    }
    return true;
  }

  private readPrefs(): AudioPrefs {
    return (this.deps.prefs ?? (() => audioPrefsStore.get()))();
  }

  private applyLevel(): void {
    if (this.out === null || this.ctx === null) return;
    try {
      this.out.gain.setTargetAtTime(this.level(), this.ctx.currentTime, 0.015);
    } catch {
      this.out.gain.value = this.level();
    }
  }

  private clock(): number {
    return (this.deps.now ?? (() => Date.now()))();
  }

  /**
   * Play an id. Returns true when a voice started. Silent false before unlock,
   * while muted, inside the id's throttle window or over the voice cap.
   */
  play(id: UiSoundId, options: { gain?: number } = {}): boolean {
    const spec = UI_SOUND_MANIFEST[id];
    if (spec === undefined || this.ctx === null || this.out === null) return false;
    if (this.level() <= 0) return false;
    const at = this.clock();
    const prev = this.last.get(id);
    if (prev !== undefined && at - prev < spec.minIntervalMs) return false;
    if (this.voices >= UI_MAX_VOICES) return false;
    this.last.set(id, at);

    this.probe(id);
    const gain = spec.gain * (options.gain ?? 1);
    try {
      const buffer = this.buffers.get(id);
      const length = buffer !== undefined ? this.playBuffer(buffer, gain) : this.playSynth(spec, gain);
      this.voices++;
      setTimeout(() => {
        this.voices = Math.max(0, this.voices - 1);
      }, Math.ceil(length * 1000) + 30);
      return true;
    } catch {
      return false;
    }
  }

  private playBuffer(buffer: AudioBuffer, gain: number): number {
    const ctx = this.ctx as Ctx;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.out as GainNode);
    src.start();
    return buffer.duration;
  }

  /** Voice a recipe. Returns its length in seconds. */
  private playSynth(spec: UiSoundSpec, busGain: number): number {
    const ctx = this.ctx as Ctx;
    const t0 = ctx.currentTime + 0.002;
    let end = 0;
    for (const layer of spec.layers) {
      const start = t0 + (layer.delay ?? 0);
      const attack = Math.max(0.001, layer.attack ?? 0.003);
      const stop = start + attack + layer.decay + 0.02;
      end = Math.max(end, stop - t0);

      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, start);
      env.gain.linearRampToValueAtTime(Math.max(0.0002, layer.gain * busGain), start + attack);
      env.gain.exponentialRampToValueAtTime(0.0001, start + attack + layer.decay);
      env.connect(this.out as GainNode);

      if (layer.kind === 'noise') {
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer();
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.Q.value = layer.q ?? 1;
        bp.frequency.setValueAtTime(layer.freq, start);
        if (layer.to !== undefined) bp.frequency.exponentialRampToValueAtTime(layer.to, start + attack + layer.decay);
        src.connect(bp).connect(env);
        src.start(start);
        src.stop(stop);
      } else {
        const osc = ctx.createOscillator();
        osc.type = layer.kind;
        osc.frequency.setValueAtTime(layer.freq, start);
        if (layer.to !== undefined) osc.frequency.exponentialRampToValueAtTime(layer.to, start + attack + layer.decay);
        if (layer.filter !== undefined) {
          const lp = ctx.createBiquadFilter();
          lp.type = 'lowpass';
          lp.frequency.value = layer.filter;
          osc.connect(lp).connect(env);
        } else {
          osc.connect(env);
        }
        osc.start(start);
        osc.stop(stop);
      }
    }
    return end;
  }

  private noiseBuffer(): AudioBuffer {
    if (this.noise !== null) return this.noise;
    const ctx = this.ctx as Ctx;
    const len = Math.floor(ctx.sampleRate * 0.5);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // A fixed LCG, not Math.random: the same hiss every session, and nothing
    // here is gameplay (shared/sim's no-random law does not reach the shell,
    // but a deterministic texture is easier to tune by ear anyway).
    let seed = 0x2f6b9a1d;
    for (let i = 0; i < len; i++) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      data[i] = (seed / 0xffffffff) * 2 - 1;
    }
    this.noise = buf;
    return buf;
  }

  /** Look for a recorded file for `id`, once per session, in the background. */
  private probe(id: UiSoundId): void {
    if ((this.files.get(id) ?? 'unknown') !== 'unknown') return;
    this.files.set(id, 'probing');
    const fetchFile = this.deps.fetchFile ?? defaultFetch;
    const ctx = this.ctx;
    if (ctx === null) return;
    const tryAt = async (i: number): Promise<void> => {
      const urls = uiSoundFiles(id);
      if (i >= urls.length) {
        this.files.set(id, 'none');
        return;
      }
      const bytes = await fetchFile(urls[i] as string).catch(() => null);
      if (bytes === null) return tryAt(i + 1);
      try {
        const buffer = await ctx.decodeAudioData(bytes);
        this.buffers.set(id, buffer);
        this.files.set(id, 'none');
      } catch {
        return tryAt(i + 1);
      }
    };
    void tryAt(0);
  }

  /** True once a recorded file replaced the synth for `id`. */
  hasRecording(id: UiSoundId): boolean {
    return this.buffers.has(id);
  }

  destroy(): void {
    this.unsub?.();
    this.unsub = null;
    try {
      void this.ctx?.close();
    } catch {
      /* already closed */
    }
    this.ctx = null;
    this.out = null;
  }
}

function clamp01(v: number): number {
  return !(v > 0) ? 0 : v > 1 ? 1 : v;
}

function defaultContext(): Ctx | null {
  const g = globalThis as unknown as {
    AudioContext?: new () => Ctx;
    webkitAudioContext?: new () => Ctx;
  };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (typeof Ctor !== 'function') return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

/**
 * Fetch a slot, refusing anything that is not audio. The content-type check is
 * the SPA-fallback guard: a dev server answers a missing file with index.html
 * and a 200, which would otherwise reach the decoder as garbage.
 */
async function defaultFetch(url: string): Promise<ArrayBuffer | null> {
  if (typeof fetch !== 'function') return null;
  const res = await fetch(url);
  if (!res.ok) return null;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('text/html')) return null;
  return res.arrayBuffer();
}

/* ═══════════════════════════════════════════════════════════ singleton ══ */

let shared: UiSoundBus | null = null;

export function uiSoundBus(): UiSoundBus {
  shared ??= new UiSoundBus();
  return shared;
}

/** Unlock the shared bus. Call from a gesture. */
export function unlockUiSounds(): boolean {
  return uiSoundBus().unlock();
}

/** Fire-and-forget. Never throws; false before the first gesture. */
export function playUiSound(id: UiSoundId, options: { gain?: number } = {}): boolean {
  try {
    return uiSoundBus().play(id, options);
  } catch {
    return false;
  }
}

/** Test seam. */
export function resetUiSoundsForTests(bus: UiSoundBus | null = null): void {
  shared?.destroy();
  shared = bus;
}
